import { FC, useMemo } from 'react';
import { Card, Col, Popover, Progress, Row, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { differenceInCalendarDays } from 'date-fns';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { useAppSelector } from 'hooks';

import { Object, OverviewBlockProps } from 'models';

import Utils from 'utils/utils';
import { LoyaltyIcons } from 'utils/constants';

import { Skeleton } from 'componets/ui';

import { ReactComponent as BasicIcon } from 'assets/icons/basic-icon.svg';
import { ReactComponent as PlatinumIcon } from 'assets/icons/platinum-circle.svg';

import { selectUser } from 'selectors';

import styles from './index.module.scss';

const DAYS = 90;

const TEXTS: Object = {
	Silver: i18n.t('loyaltySilverText'),
	Gold: i18n.t('loyaltyGoldText'),
	Platinum: i18n.t('loyaltyPlatinumText'),
};

export const LoyaltyStatus: FC<OverviewBlockProps> = ({ isLoading }) => {
	const { loyalty, data: overview } = useAppSelector((state) => state.overview);
	const user = useAppSelector(selectUser);

	const { t } = useTranslation();

	const balance = +overview.totalBalance;

	const currentPlan = user.category;

	const nextPlan = useMemo(
		() => loyalty?.find((plan) => plan.locked) || loyalty[loyalty.length - 1],
		[loyalty],
	);

	const plan = loyalty?.find((item) => item.title === currentPlan?.title);

	const Icon = plan ? LoyaltyIcons[plan?.title] : currentPlan?.slug === 'default' ? BasicIcon : '';
	const text = plan
		? TEXTS[plan?.title]
		: currentPlan?.slug === 'default'
		? t('loyaltyStatusBasicText')
		: '';

	const isPlatinum = plan?.title === 'Platinum';

	const isBasic = !currentPlan?.isLoyalty && !currentPlan?.isDefault;

	const loyaltyLink = Utils.getLinkLanguage(
		'help-category/operating-principles-of-the-loyalty-program',
		'lt/duk-category/kaip-veikia-lojalumo-programa',
	);

	const percent = isPlatinum
		? (+balance / plan?.minAmount) * 100
		: (+balance / nextPlan?.minAmount) * 100;

	const days = differenceInCalendarDays(new Date(), new Date(user.registrationDate));
	const isUnavailableLoyalty = days < DAYS;

	const statusLabel = (
		<Typography.Text>
			{t('yourStatusText')}&nbsp;
			<Popover content={t('loyaltyStatusTooltipText')} placement="top">
				<InfoCircleOutlined style={{ cursor: 'pointer' }} />
			</Popover>
		</Typography.Text>
	);

	return (
		<Card
			bordered={false}
			className={classNames(styles.root, {
				[styles.black]: isPlatinum && !isUnavailableLoyalty,
			})}
		>
			<Skeleton loading={isLoading}>
				{isPlatinum && !isUnavailableLoyalty ? (
					<Col className={styles.col}>
						<PlatinumIcon />
						<Col className={styles.platinum}>
							<Typography.Title type="secondary" level={4}>
								{t('platinumCategoryText')}
							</Typography.Title>
							<Typography.Text className={styles.text} type="secondary">
								{text}
								<a href={loyaltyLink} className={styles.link} target="_blank" rel="noreferrer">
									{t('readMoreText')}
								</a>
							</Typography.Text>
						</Col>
					</Col>
				) : isBasic ? (
					<div className={styles.basicWrapper}>
						<Row gutter={[0, 0]} style={{ marginBottom: 18 }}>
							<Col style={{ width: '100%' }}>
								<Typography.Title level={4}>{t('loyaltyStatusText')}</Typography.Title>

							<Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
								{statusLabel}
								<BasicIcon />
							</Row>

								<Row justify="space-between" align="middle">
									<Typography.Text>{t('yourPersonalBonusText')}:</Typography.Text>
									<Typography.Text type={+currentPlan?.interestRate >= 0 ? 'success' : 'danger'}>
									{+currentPlan?.interestRate >= 0 ? '+' : ''}
									{currentPlan?.interestRate}%
								</Typography.Text>
								</Row>
							</Col>
						</Row>
						<Typography.Text type="secondary" className={styles.readMore}>
							<a href={loyaltyLink} className={styles.link} target="_blank" rel="noreferrer">
								{t('readMoreText')}
							</a>
						</Typography.Text>
					</div>
				) : (
					<>
						<Row gutter={[0, 0]} style={{ marginBottom: 18 }}>
							<Col style={{ width: '100%' }}>
								<Typography.Title level={4}>{t('loyaltyStatusText')}</Typography.Title>

								{isUnavailableLoyalty
									? ''
									: Icon && (
											<Row justify="space-between" align="middle" style={{ marginBottom: 32 }}>
												{statusLabel}
												<Icon />
											</Row>
									  )}

								<Typography.Text>
									{isUnavailableLoyalty ? t('unavailableLoyaltyStatusText') : text}{' '}
									<a href={loyaltyLink} className={styles.link} target="_blank" rel="noreferrer">
										{t('readMoreText')}
									</a>
								</Typography.Text>
							</Col>
						</Row>
						<Row gutter={[0, 12]} justify="space-between">
							<Col span={24}>
								<Progress
									strokeColor={{
										'0%': '#65ABFF',
										'100%': '#FB827A',
									}}
									percent={isUnavailableLoyalty ? 0 : percent}
									showInfo={false}
								/>
							</Col>

							{isUnavailableLoyalty ? (
								<Col>
									<Typography.Text>
										<Trans
											i18nKey="daysLeftKickOffText"
											values={{
												days: DAYS - days,
												textDays: DAYS - days === 1 ? t('dayText') : t('daysText'),
											}}
										/>
									</Typography.Text>
								</Col>
							) : (
								<Col className={styles.values}>
									<Col className={styles.row}>
										<Typography.Text>
											{Utils.moneyFormat(balance)}/
										</Typography.Text>
										<Typography.Text>
											{Utils.moneyFormat(Number(nextPlan?.minAmount))}
										</Typography.Text>
									</Col>
									<Col className={styles.row}>
										<Typography.Text>{nextPlan?.title}&nbsp;</Typography.Text>
										<Typography.Text>(+{nextPlan?.percent}%)</Typography.Text>
									</Col>
								</Col>
							)}
						</Row>
					</>
				)}
			</Skeleton>
		</Card>
	);
};
